// MOTEUR DE RENDU UNIQUE d'un fil de discussion — CCT-99.
//
// Il existait trois rendus de chat (scénarisé, US, personnage) qui divergeaient : nom et avatar
// absents ici, bulle du joueur qui débordait là. Un seul composant sert désormais tous les
// écrans ; corriger un défaut le corrige partout.
import React from 'react';
import { Image, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import RichText from './RichText';
import DiagramViewer from './DiagramViewer';
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import Ionicons from 'react-native-vector-icons/Ionicons';

import { type Palette, TYPE_COLOR, glow, glowText } from '../utils/cyb';
import { usePalette } from '../hooks/usePalette';
import { useT } from '../i18n/useT';

const ACCENT = TYPE_COLOR.chat;
// Palette de la CONVERSATION (référence : capture du chat « Un coup de pouce »). Les bulles sont
// des VOILES — fond teinté + bord lumineux — jamais des aplats pleins : un aplat écrase le texte
// et fait diverger le chat du reste de l'app (dashboard, quiz, cartes). Deux couleurs pour
// distinguer les speakerColors d'un coup d'œil : VERT pour le personnage, CYAN pour le joueur.
//
// ⚠️ C'ÉTAIT UNE CONSTANTE DE MODULE LISANT LA PALETTE SOMBRE. Le vert et le cyan néon sont
// calculés pour un fond nuit ; en thème clair ils tombaient à ~1,4:1 sur blanc et le liseré des
// bulles devenait un trait fluo illisible. La paire se dérive donc de la palette EN COURS.
const speakerColors = (C: Palette) => ({ character: C.green, me: C.cyan });

export interface ThreadMessage {
  role: 'character' | 'student';
  text: string;
  /** Horodatage ISO ; vide = pas d'heure affichée (messages rejoués du scénario). */
  at?: string;
  /** Locuteur réel : un chat de scénario peut faire parler plusieurs personnages. */
  speaker?: string;
  /** URL d'avatar du locuteur ; à défaut, l'initiale sur pastille. */
  avatar?: string | null;
  /** Séparateur affiché AVANT ce message (mission, jour, chapitre…). */
  separator?: string;
  /** Avatar local (`require`) quand l'image n'est pas une URL — chat scénarisé bundlé. */
  avatarSource?: any;
  /**
   * A draw.io diagram shown INSIDE this turn's bubble (CCT-477).
   *
   * ⚠️ IT GOES THROUGH THE SINGLE RENDERER, like everything else about a chat line. Drawn by the
   * calling screen instead, the diagram would sit outside the bubble on one screen and inside it
   * on the next — the very divergence this component was built to end.
   */
  diagramUrl?: string;
  /** The author's zoom for that diagram, in percent. */
  diagramZoom?: number;
}

export interface ThreadOption { id: string; label: string }

const hhmm = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const Avatar: React.FC<{ url?: string | null; name?: string; size?: number }> = ({ url, name, size = 32 }) => {
  const box = { width: size, height: size, borderRadius: size / 2 };
  return url
    ? <Image source={{ uri: url }} style={box} resizeMode="cover" />
    : <View style={[box, st.avatarFallback]}>
        <Text style={st.avatarText}>{(name || '?').slice(0, 1).toUpperCase()}</Text>
      </View>;
};

/** Mode de chat : ce qui change entre les écrans est un PARAMÈTRE, pas un composant.
 *  - `mission`   : chat scénarisé de la storyline ;
 *  - `character` : centre de messagerie (chronologie d'un personnage) ;
 *  - `us`        : chat du moteur attaché à une user story. */
export type ChatMode = 'mission' | 'character' | 'us';

/** Contexte d'US injecté dans la question (mode `character`). Absent = fonction désactivée. */
export interface ChatContext<T = any> {
  items: T[];
  selected: T | null;
  onSelect: (item: T) => void;
  onClear: () => void;
  /** Ouvert au montage ; le joueur peut replier. */
  open?: boolean;
  keyOf: (item: T) => string | number;
  labelOf: (item: T) => string;
  subLabelOf?: (item: T) => string;
  badgeOf?: (item: T) => React.ReactNode;
  title?: string;
}

export interface ChatThreadProps {
  mode: ChatMode;
  /** Sélection d'une US comme context de la question — activée par sa seule présence. */
  context?: ChatContext;
  messages: ThreadMessage[];
  /** Nom par défaut du personnage quand un message ne porte pas de locuteur. */
  characterName?: string | null;
  characterAvatar?: string | null;
  playerAvatar?: any;
  playerName?: string | null;
  options?: ThreadOption[];
  optionsHint?: string;
  busy?: boolean;
  /** Le personnage rédige : bulle « … » À LA PLACE DE SA PROCHAINE RÉPLIQUE, sous le message
   *  que le joueur vient d'envoyer. Un spinner de fenêtre ne dit pas QUI on attend ; une bulle
   *  posée avant la réponse du joueur ne dit pas non plus à quoi il réfléchit. */
  awaiting?: boolean;
  onChoose?: (id: string) => void;
  /** Bloc libre inséré au-dessus du panneau de choix (ex. context sélectionné). */
  footer?: React.ReactNode;
  scrollRef?: React.RefObject<ScrollView | null>;
  emptyLabel?: string;
}

/** UNE ligne de message. Exportée pour les écrans qui INTERCALENT autre chose entre les
 *  messages (chat scénarisé : questions, panneaux de choix) et ne peuvent donc pas déléguer
 *  toute la liste — sans ça, ils redéveloppent la bulle et l'affichage diverge. */
export const ThreadRow: React.FC<{
  m: ThreadMessage;
  characterName?: string | null;
  characterAvatar?: string | null;
  /** Image locale (require) de l'avatar joueur. */
  playerAvatar?: any;
  /** Nom affiché au-dessus de la bulle du joueur (CCT-151) : quand l'auteur a désigné un
   *  personnage jouable, le joueur EST ce personnage — il doit porter son nom, pas « Moi ». */
  playerName?: string | null;
  /** Retour de correction : colore la bulle du personnage sans changer sa forme. */
  tone?: 'ok' | 'ko';
}> = ({ m, characterName, characterAvatar, playerAvatar, playerName, tone }) => {
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const SAY = speakerColors(usePalette());
  const isUser = m.role === 'student';
  const name = m.speaker || characterName || 'Personnage';
  const toneBg = tone === 'ok' ? (isDarkTheme ? '#064E3B' : '#D1FAE5')
    : tone === 'ko' ? (isDarkTheme ? '#7F1D1D' : '#FEE2E2') : undefined;
  const toneBorder = tone === 'ok' ? '#10B981' : tone === 'ko' ? '#EF4444' : undefined;

  return (
    <View>
      {!!m.separator && (
        <View style={st.sep}>
          <Text style={[st.sepText, { color: colors.textTertiary, backgroundColor: colors.surfaceSecondary }]}>
            {m.separator}
          </Text>
        </View>
      )}
      <View style={[st.row, isUser ? st.rowRight : st.rowLeft]}>
        {!isUser && (
          <View style={st.avatarWrap}>
            {m.avatarSource
              ? <Image source={m.avatarSource} style={st.avatarImg} resizeMode="cover" />
              : <Avatar url={m.avatar ?? characterAvatar} name={name} />}
          </View>
        )}
        {/* `flexShrink` + `minWidth: 0` : sans eux, une réplique longue du joueur pousse
            la bulle hors de l'écran au lieu de passer à la ligne. */}
        <View style={[st.bubbleCol, isUser ? st.colRight : st.colLeft]}>
          {!isUser && <Text style={[st.sender, { color: colors.textSecondary }]}>{name}</Text>}
          {isUser && !!playerName && (
            <Text style={[st.sender, { color: colors.textSecondary, alignSelf: 'flex-end' }]}>{playerName}</Text>
          )}
          <View style={[st.bubble, isUser ? st.bubbleMe : st.bubbleOther,
            toneBg
              ? { backgroundColor: toneBg, borderColor: toneBorder }
              : glow(isUser ? SAY.me : SAY.character, true, isDarkTheme)]}>
            {/* CCT-477 — the diagram comes FIRST, then what the character says about it. The
                other order reads as a caption looking for its picture. */}
            {m.diagramUrl ? <DiagramViewer url={m.diagramUrl} hauteur={220} zoom={m.diagramZoom} /> : null}
            {/* ⚠️ A DIAGRAM-ONLY TURN CARRIES NO TEXT, and an empty `RichText` still reserves a
                line: the bubble came out with a blank strip under the frame. */}
            {m.text ? (
              <RichText style={[st.text, { color: colors.textPrimary }]} value={m.text} />
            ) : null}
          </View>
          {!!hhmm(m.at) && (
            <Text style={[st.time, { color: colors.textTertiary }, isUser && st.timeRight]}>
              {hhmm(m.at)}
            </Text>
          )}
        </View>
        {isUser && (
          <View style={st.avatarWrap}>
            {playerAvatar
              ? <Image source={playerAvatar} style={st.avatarImg} resizeMode="cover" />
              : <Avatar name={playerName ?? 'Moi'} />}
          </View>
        )}
      </View>
    </View>
  );
};

/** Panneau de choix du joueur — LE panneau, pour tous les modes de chat.
 *
 *  C'est la variante RICHE du chat scénarisé (réponse déjà tentée, verrouillée, en attente)
 *  qui fait référence : les autres chats appellent le même composant avec ces options non
 *  renseignées et obtiennent le comportement simple. L'inverse — un panneau simple d'un côté,
 *  un riche de l'autre — est exactement ce qui a fait diverger les trois écrans.
 */
export const OptionsPanel: React.FC<{
  options: ThreadOption[];
  hint?: string;
  /** Aucune interaction (tour déjà joué) : la sélection reste visible, verrouillée. */
  readOnly?: boolean;
  /** Une réponse est partie, on attend le personnage : plus rien n'est cliquable. */
  busy?: boolean;
  selectedLabel?: string | null;
  selectedLabels?: string[];
  /** Réponses DÉJÀ essayées sur ce tour : proposées grisées, pas re-cliquables. */
  attempted?: string[];
  pendingLabel?: string | null;
  playerAvatar?: any;
  /** Nom du joueur (CCT-151) : le panneau porte SON avatar, donc SON nom. */
  playerName?: string | null;
  onChoose?: (opt: ThreadOption) => void;
}> = ({ options, hint, readOnly = false, busy = false, selectedLabel = null,
        selectedLabels = [], attempted = [], pendingLabel = null, playerAvatar, playerName, onChoose }) => {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const SAY = speakerColors(usePalette());
  if (!options.length) return null;
  const pending = !readOnly && busy && pendingLabel ? pendingLabel : null;

  return (
    <View style={[st.row, st.rowRight]}>
      <View style={[st.bubbleCol, st.colRight, st.choiceCol]} pointerEvents={readOnly ? 'none' : 'auto'}>
        <Text style={[st.optHint, { color: colors.textSecondary }]}>
          {hint ?? t('thread.pickAnswerToContinue')}
        </Text>
        {options.map(opt => {
          const isPending = pending === opt.label;
          const isSelected = isPending || selectedLabel === opt.label || selectedLabels.includes(opt.label);
          const isTried = attempted.includes(opt.label) && !isPending;
          const isDisabled = (busy && !isPending) || (isTried && !isSelected) || (readOnly && !isSelected);

          if (isSelected && (readOnly || isPending)) {
            return (
              // La réponse RETENUE est la parole du JOUEUR : elle prend sa couleur (cyan),
              // comme sa bulle. En vert, elle se lisait comme une réplique du personnage.
              <View key={opt.id} style={[st.opt, { borderColor: SAY.me },
                glow(SAY.me, true, isDarkTheme)]}>
                <View style={st.checkRow}>
                  <Ionicons name="checkmark" size={14} color={SAY.me} style={st.checkIcon} />
                  <Text style={[st.optText, { color: glowText(SAY.me, true, isDarkTheme) }]}>{opt.label}</Text>
                </View>
              </View>
            );
          }

          if (isSelected && !readOnly && !isPending && onChoose) {
            return (
              <TouchableOpacity key={opt.id} activeOpacity={0.7} onPress={() => onChoose(opt)}
                style={[st.opt, glow(SAY.me, true, isDarkTheme)]}>
                <View style={st.checkRow}>
                  <View style={[st.checkbox, { borderColor: SAY.me, backgroundColor: `${SAY.me}1F` }]}>
                    <Ionicons name="checkmark" size={11} color={SAY.me} />
                  </View>
                  <Text style={[st.optText, { color: glowText(SAY.me, true, isDarkTheme) }]}>{opt.label}</Text>
                </View>
              </TouchableOpacity>
            );
          }

          if (isDisabled) {
            return (
              <View key={opt.id} style={[st.opt, st.optDisabled,
                { borderColor: colors.border, backgroundColor: colors.surfaceSecondary }]}>
                <Text style={[st.optText, { color: colors.textTertiary }]}>{opt.label}</Text>
              </View>
            );
          }

          return (
            <TouchableOpacity key={opt.id} activeOpacity={0.7} onPress={() => onChoose?.(opt)}
              style={[st.opt, glow(SAY.me, true, isDarkTheme)]}>
              <Text style={[st.optText, { color: glowText(SAY.me, true, isDarkTheme) }]}>{opt.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
      <View style={st.avatarWrap}>
        {playerAvatar ? <Image source={playerAvatar} style={st.avatarImg} resizeMode="cover" />
                      : <Avatar name={playerName ?? 'Moi'} />}
      </View>
    </View>
  );
};

/** Trois points animés : le personnage rédige. */
const Typing: React.FC<{ color: string }> = ({ color }) => {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setN(v => (v + 1) % 4), 400);
    return () => clearInterval(t);
  }, []);
  return <Text style={[st.text, { color }]}>{'.'.repeat(n || 1)}</Text>;
};

export default function ChatThread({
  mode, context, messages, characterName, characterAvatar, playerAvatar, playerName, options = [],
  optionsHint, busy, awaiting, onChoose, footer, scrollRef, emptyLabel,
}: ChatThreadProps) {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const SAY = speakerColors(usePalette());
  const [ctxOpen, setCtxOpen] = React.useState(context?.open ?? true);
  // À l'ouverture, on veut la FIN de la discussion : c'est le dernier échange qui appelle une
  // action, pas le premier. `onContentSizeChange` plutôt qu'un effet au montage — la hauteur
  // n'est connue qu'une fois les bulles mesurées.
  const innerRef = React.useRef<ScrollView>(null);
  const ref = scrollRef ?? innerRef;
  const settled = React.useRef(false);

  const thread = (
    <ScrollView
      ref={ref}
      style={st.scroll}
      contentContainerStyle={st.content}
      onContentSizeChange={() => {
        if (settled.current) return;
        settled.current = true;
        ref.current?.scrollToEnd({ animated: false });
      }}
    >
      {messages.map((m, i) => (
        <ThreadRow key={i} m={m} characterName={characterName}
          characterAvatar={characterAvatar} playerAvatar={playerAvatar} playerName={playerName} />
      ))}

      {messages.length === 0 && !!emptyLabel && (
        <Text style={[st.dim, { color: colors.textTertiary }]}>{emptyLabel}</Text>
      )}

      {/* Indicateur de rédaction, à la place et au nom du personnage. */}
      {awaiting && (
        <View style={[st.row, st.rowLeft]}>
          <View style={st.avatarWrap}>
            <Avatar url={characterAvatar} name={characterName ?? undefined} />
          </View>
          <View style={[st.bubbleCol, st.colLeft]}>
            <Text style={[st.sender, { color: colors.textSecondary }]}>
              {characterName ?? 'Personnage'}
            </Text>
            <View style={[st.bubble, st.bubbleOther, glow(SAY.character, true, isDarkTheme)]}>
              <Typing color={colors.textSecondary} />
            </View>
          </View>
        </View>
      )}

      {footer}

      {/* Contexte sélectionné : rappelé juste au-dessus des options, car c'est lui qui
          détermine ce que le personnage va interpréter. */}
      {!awaiting && !!context?.selected && (
        <View style={[st.ctxBanner, { backgroundColor: colors.surfaceSecondary, borderColor: ACCENT }]}>
          <Ionicons name="pricetag-outline" size={13} color={ACCENT} />
          <Text numberOfLines={1} style={[st.ctxText, { color: colors.textPrimary }]}>
            Context : {context.labelOf(context.selected)}
          </Text>
          <TouchableOpacity onPress={context.onClear}>
            <Ionicons name="close" size={14} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      )}

      {!awaiting && (
        <OptionsPanel options={options} hint={optionsHint} busy={busy}
          playerAvatar={playerAvatar} playerName={playerName} onChoose={(o) => onChoose?.(o.id)} />
      )}
    </ScrollView>
  );

  if (!context) return thread;

  // Le volet de context fait partie du MÊME composant : l'activer est un paramètre, pas un
  // écran qui réimplémente sa colonne.
  return (
    <View style={st.withCtx}>
      {ctxOpen && (
        <View style={[st.ctxPanel, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <View style={st.ctxHead}>
            <Text style={[st.ctxTitle, { color: colors.textPrimary }]}>
              {context.title ?? t('thread.askAbout')}
            </Text>
            <TouchableOpacity onPress={() => setCtxOpen(false)}>
              <Ionicons name="chevron-back" size={16} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 8 }}>
            {context.items.map(it => {
              const on = context.selected && context.keyOf(context.selected) === context.keyOf(it);
              return (
                <TouchableOpacity key={context.keyOf(it)} onPress={() => context.onSelect(it)}
                  style={[st.ctxRow, {
                    borderColor: on ? ACCENT : colors.border,
                    backgroundColor: colors.surfaceSecondary,
                  }]}>
                  <Text numberOfLines={2} style={[st.ctxName, { color: colors.textPrimary }]}>
                    {context.labelOf(it)}
                  </Text>
                  <View style={st.ctxMeta}>
                    {context.badgeOf?.(it)}
                    {!!context.subLabelOf && (
                      <Text numberOfLines={1} style={[st.ctxSub, { color: colors.textTertiary }]}>
                        {context.subLabelOf(it)}
                      </Text>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}
            {context.items.length === 0 && (
              <Text style={[st.dim, { color: colors.textTertiary }]}>{t('thread.noTask')}</Text>
            )}
          </ScrollView>
        </View>
      )}
      {!ctxOpen && (
        <TouchableOpacity onPress={() => setCtxOpen(true)}
          style={[st.ctxHandle, { borderColor: colors.border, backgroundColor: colors.surface }]}>
          <Ionicons name="list-outline" size={16} color={colors.iconPrimary} />
        </TouchableOpacity>
      )}
      {thread}
    </View>
  );
}

const st = StyleSheet.create({
  scroll: { flex: 1, minHeight: 0 },
  content: { padding: 12 },
  sep: { alignItems: 'center', marginVertical: 10 },
  sepText: { fontSize: 11, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, overflow: 'hidden' },
  row: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginBottom: 12 },
  rowLeft: { justifyContent: 'flex-start' },
  rowRight: { justifyContent: 'flex-end' },
  avatarWrap: { width: 32, height: 32, borderRadius: 16, overflow: 'hidden', flexShrink: 0 },
  avatarImg: { width: 32, height: 32, borderRadius: 16 },
  avatarFallback: { backgroundColor: ACCENT, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontSize: 11, fontWeight: 'bold', color: '#FFFFFF' },
  bubbleCol: { maxWidth: '76%', flexShrink: 1, minWidth: 0 },
  colLeft: { alignItems: 'flex-start' },
  colRight: { alignItems: 'flex-end' },
  choiceCol: { alignSelf: 'flex-end', width: '100%', gap: 8 },
  sender: { fontSize: 11, fontWeight: '600', marginBottom: 3, marginLeft: 4 },
  // `borderWidth` porté par le style de base : les bulles sont des VOILES (fond teinté + bord),
  // le bord ne doit donc jamais dépendre de la présence d'un ton de correction.
  bubble: { borderRadius: 16, borderWidth: 1.5, paddingHorizontal: 14, paddingVertical: 10, maxWidth: '100%' },
  bubbleMe: { borderBottomRightRadius: 4 },
  bubbleOther: { borderBottomLeftRadius: 4 },
  text: { fontSize: 14, lineHeight: 20, flexShrink: 1 },
  time: { fontSize: 10, marginTop: 3, marginHorizontal: 4 },
  timeRight: { alignSelf: 'flex-end' },
  optHint: { fontSize: 12, fontWeight: '500', textAlign: 'right' },
  opt: { borderWidth: 1.5, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11, alignSelf: 'stretch' },
  optDisabled: { opacity: 0.55 },
  optLocked: { borderColor: ACCENT },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  checkIcon: { marginRight: 2 },
  checkbox: { width: 18, height: 18, borderRadius: 4, borderWidth: 1.5,
              alignItems: 'center', justifyContent: 'center' },
  optText: { fontSize: 13, fontWeight: '500', lineHeight: 18 },
  dim: { fontSize: 12, textAlign: 'center', marginTop: 8 },
  withCtx: { flex: 1, minHeight: 0, flexDirection: 'row', alignItems: 'stretch' },
  // Colonne FIXE : `height: '100%'` + `minHeight: 0` la détachent du flux du fil — sans quoi
  // elle suit le défilement des messages au lieu de rester en regard.
  ctxPanel: { width: 250, height: '100%', minHeight: 0, borderRightWidth: 1, padding: 10, gap: 8 },
  ctxHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  ctxTitle: { fontSize: 12, fontWeight: '700' },
  ctxRow: { borderWidth: 1, borderRadius: 10, padding: 8, gap: 6 },
  ctxName: { fontSize: 12, fontWeight: '600', lineHeight: 16 },
  ctxMeta: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  ctxSub: { fontSize: 10, flex: 1 },
  ctxHandle: { width: 34, borderRightWidth: 1, alignItems: 'center', paddingTop: 10 },
  ctxBanner: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1,
               borderRadius: 10, paddingHorizontal: 10, paddingVertical: 7, marginBottom: 10 },
  ctxText: { fontSize: 12, flex: 1 },
});
